import { Box, Popover, TextField, Typography } from '@mui/material'
import type { Theme } from '@mui/system'
import SpinnerButton from 'components/SpinnerButton'
import { useCreateOrganization } from 'hooks/useOrganizations'
import debounce from 'lodash/debounce'
import type { FC } from 'react'
import { useCallback, useMemo, useRef, useState, useTransition } from 'react'
import type { CustomNodeElementProps } from 'react-d3-tree'
import Tree from 'react-d3-tree'
import type { CreateOrganizationDTO, ExpandedOrganizationHierarchy } from 'types/organization'
import TreeNodeComponent from './components/TreeNode'
import { useTreeState } from './hooks/useTreeState'
import { useTreeTranslate } from './hooks/useTreeTranslate'
import type { TreeNode } from './hooks/useTreeState'
import { getManagerName } from './utils'

interface OrganizationTreeProps {
  organizationHierarchy: ExpandedOrganizationHierarchy
  setSelectedOrganization: (id: string) => void
  theme: Theme
  token: string
  onOrganizationCreated: (organizationDTO: CreateOrganizationDTO) => Promise<void>
  setNeedsRefresh: (needsRefresh: boolean) => void
  isSubmitting: boolean
}

const OrganizationTree: FC<OrganizationTreeProps> = ({
  organizationHierarchy,
  setSelectedOrganization,
  theme,
  token,
  onOrganizationCreated,
  setNeedsRefresh,
  isSubmitting,
}) => {
  const { treeData, collapsedNodes, zoomLevel, toggleNodeCollapse } = useTreeState(organizationHierarchy)
  const { translate, treeContainerRef } = useTreeTranslate(treeData)

  const [newOrgName, setNewOrgName] = useState('')
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null)
  const [hoveredNodeId, setHoveredNodeId] = useState<string | null>(null)
  const [anchorPosition, setAnchorPosition] = useState<{ top: number; left: number } | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const [isPopoverOpen, setIsPopoverOpen] = useState(false)
  const [selectedNodeName, setSelectedNodeName] = useState<string>('')
  const [isPending, startTransition] = useTransition()

  const { error: createError } = useCreateOrganization(token)

  const handleToggleCollapse = useCallback(
    (nodeId: string, event: React.MouseEvent) => {
      event.stopPropagation()
      toggleNodeCollapse(nodeId)
    },
    [toggleNodeCollapse]
  )

  const handleNodeClick = useCallback(
    (nodeData: CustomNodeElementProps) => {
      const treeNode = nodeData.nodeDatum as TreeNode
      startTransition(() => {
        setSelectedNodeId(treeNode.id)
        setSelectedNodeName(treeNode.visible ? treeNode.name : getManagerName(treeNode.users) || 'Manager Name')
        setSelectedOrganization(treeNode.id)
      })

      if ((nodeData as any).__rd3t && (nodeData as any).__rd3t.nodeElement) {
        const nodeElement = (nodeData as any).__rd3t.nodeElement as SVGGElement
        const { left, top, width, height } = nodeElement.getBoundingClientRect()

        setAnchorPosition({
          left: left + width / 2,
          top: top + height + 20,
        })
      } else {
        setAnchorPosition({
          left: window.innerWidth / 2,
          top: 270,
        })
      }
      setIsPopoverOpen(true)
    },
    [setSelectedOrganization]
  )

  const handleClosePopover = () => {
    setAnchorPosition(null)
    setNewOrgName('')
    setIsPopoverOpen(false)
  }

  const handleCreateOrganization = useCallback(
    async (organizationDTO: CreateOrganizationDTO) => {
      if (isSubmitting) {
        console.log('Submission in progress, please wait...')
        return
      }
      await onOrganizationCreated(organizationDTO)
    },
    [isSubmitting, onOrganizationCreated]
  )

  const debouncedHandleCreateOrganization = debounce(handleCreateOrganization, 300, { leading: true, trailing: false })

  const handleAddOrganization = async () => {
    if (newOrgName && selectedNodeId) {
      await debouncedHandleCreateOrganization({
        name: newOrgName,
        parentId: selectedNodeId,
        visible: true,
        status: 'CHILD',
      })
      handleClosePopover()
    }
  }

  const handleNodeSelect = useCallback(
    (nodeId: string, nodeName: string) => {
      startTransition(() => {
        setSelectedNodeId(nodeId)
        setSelectedNodeName(nodeName)
        setSelectedOrganization(nodeId)
      })
    },
    [setSelectedOrganization]
  )

  const renderCustomNodeElement = useCallback(
    (rd3tProps: CustomNodeElementProps) => {
      const nodeData = rd3tProps.nodeDatum as TreeNode
      const hasChildren =
        (nodeData.children && nodeData.children.length > 0) || (nodeData._children && nodeData._children.length > 0)
      const isCollapsed = collapsedNodes.has(nodeData.id)
      const childCount = nodeData._children?.length || nodeData.children?.length || 0

      return (
        <TreeNodeComponent
          nodeData={nodeData}
          isCollapsed={isCollapsed}
          isHovered={hoveredNodeId === nodeData.id}
          hasChildren={!!hasChildren}
          childCount={childCount}
          theme={theme}
          onToggleCollapse={(e) => handleToggleCollapse(nodeData.id, e)}
          onNodeSelect={() => handleNodeSelect(nodeData.id, nodeData.name)}
          onAddTeam={() => handleNodeClick(rd3tProps)}
          onMouseEnter={() => setHoveredNodeId(nodeData.id)}
          onMouseLeave={() => setHoveredNodeId(null)}
        />
      )
    },
    [hoveredNodeId, collapsedNodes, handleToggleCollapse, handleNodeSelect, handleNodeClick, theme]
  )

  const collapsedTreeData = useMemo(() => {
    const collapseNode = (node: TreeNode): TreeNode => {
      if (collapsedNodes.has(node.id)) {
        return { ...node, _children: node.children, children: undefined }
      }
      return {
        ...node,
        children: node.children?.map(collapseNode),
        _children: undefined,
      }
    }
    return treeData ? collapseNode(treeData) : null
  }, [treeData, collapsedNodes])

  const treeStyles = useMemo(
    () => ({
      '.tree-link': {
        stroke: theme.palette.mode === 'dark' ? theme.palette.grey[300] : theme.palette.grey[300],
        strokeWidth: 2,
      },
    }),
    [theme]
  )

  if (!collapsedTreeData) {
    return <Typography>No organization data available</Typography>
  }

  return (
    <Box
      sx={{
        flexGrow: 1,
        position: 'relative',
        height: '100%',
        overflow: 'hidden',
        ...treeStyles,
      }}
      ref={treeContainerRef}
    >
      {isPending ? (
        <Typography>Loading...</Typography>
      ) : (
        <Tree
          data={collapsedTreeData}
          orientation="vertical"
          pathFunc="step"
          renderCustomNodeElement={renderCustomNodeElement}
          nodeSize={{ x: 280, y: 100 }}
          separation={{ siblings: 1.1, nonSiblings: 1.5 }}
          translate={translate}
          centeringTransitionDuration={0}
          collapsible={false}
          initialDepth={Infinity}
          zoom={zoomLevel}
          scaleExtent={{ min: 0.1, max: 1 }}
          transitionDuration={0}
          enableLegacyTransitions={false}
          pathClassFunc={() => 'tree-link'}
        />
      )}
      <Popover
        open={isPopoverOpen}
        anchorReference="anchorPosition"
        anchorPosition={anchorPosition || undefined}
        onClose={handleClosePopover}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
      >
        <Box sx={{ p: 2, width: 250 }}>
          <Typography variant="h6" sx={{ textAlign: 'center', mb: 2 }}>
            Create team under{' '}
            <Typography component="span" color="primary" fontWeight="bold">
              {selectedNodeName}
            </Typography>
          </Typography>
          <TextField
            fullWidth
            variant="standard"
            label="Team Name"
            value={newOrgName}
            onChange={(e) => setNewOrgName(e.target.value)}
            margin="normal"
            onKeyPress={(e) => e.key === 'Enter' && handleAddOrganization()}
            inputRef={inputRef}
          />
          {createError && <Typography color="error">{createError.message}</Typography>}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
            <SpinnerButton
              variant="contained"
              onClick={handleAddOrganization}
              loading={isSubmitting}
              disabled={!newOrgName.trim() || isSubmitting}
            >
              Create Team
            </SpinnerButton>
          </Box>
        </Box>
      </Popover>
    </Box>
  )
}

export default OrganizationTree
